import React, { Suspense, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import edit from '../../assets/img/edit.png';
import bin from '../../assets/img/bin.png';
import ImageTag from '../../library/ImageTag/ImageTag';
import CreatePresetForm from '../../components/BtAdminPanel/ModulePresets/CreatePresetForm';
import Preset from '../../utils/ModulePresets/Preset';
import { IModuleAceess, IPreset } from '../../utils/ModulePresets/preset.interface';
import ReactLoading from 'react-loading';
import EditPresetForm from '../../components/BtAdminPanel/ModulePresets/EditPresetForm';
import ShowPreset from '../../components/BtAdminPanel/ModulePresets/ShowPreset';
import ConfirmationPopUp from '../../library/ConfirmationPopUp/ConfirmationPopUp';
import { IPPParagraph } from '../../library/Heading/Heading';
import Button from '../../library/Button/button';
import CustomPopup from '../../components/BtAdminPanel/CustomPopup';
import IModule from '../../utils/Module/Module.interface';
import Pagination from '../../library/Pagination/Pagination';
import ConditionalLoader from '../../components/ConditionalLoader';
import Search from '../../components/Search';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import {
  changePreset,
  createPreset,
  deletePreset,
  getAllPresets
} from '../../store/slice/presetSlice';

interface IProps {
  allModules: IModule[];
}

// To show the list of all presets available
const PresetModules: React.FC<IProps> = ({ allModules }) => {
  const dispatch = useAppDispatch();
  const presetState = useAppSelector((state) => state.presets);
  const presets = presetState.data ?? [];
  const isLoading = !presetState.data && presetState.status === 'loading';
  const customModules = allModules.filter((module) => module.isSystemModule);

  const [showCreatePopup, setShowCreatePopup] = useState<boolean>(false);
  const [showModuleListPopup, setShowModuleListPopup] = useState<boolean>(false);
  const [showEditModuleListPopup, setShowEditModuleListPopup] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [showDeleteConfirmation, setshowDeleteConfirmation] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [selectedPreset, setSelectedPreset] = useState<IPreset>();
  const [query, setQuery] = useState<string>('');
  const location = useLocation();
  const highlightPreset = location.state?.highlightPreset ?? null;
  const highlightPage = location.state?.page ?? null;
  const [selectedPresetFromSearch, setSelectedPresetFromSearch] = useState<string | null>(
    highlightPreset
  );
  const [currentPage, setCurrentPage] = useState<number>(1);

  const screenHeight = window.innerHeight;
  const recordPerPage = screenHeight ? Math.ceil((screenHeight - 210) / 90) : 10; // calculate based on height of screen
  const totalPages = Math.ceil(presets.length / recordPerPage);
  const filteredPresets = presets
    .filter((preset: { presetName: string }) =>
      preset.presetName.toLowerCase().includes(query.toLowerCase())
    )
    .slice(recordPerPage * (currentPage - 1), currentPage * recordPerPage);

  const handleCloseCreatePopup = (i: boolean) => {
    setShowCreatePopup(i);
  };

  const onPageChange = (pageNumber: number) => {
    setCurrentPage(Math.min(totalPages, pageNumber));
  };

  const cancelDelete = () => {
    setshowDeleteConfirmation(false);
  };

  const confirmDelete = async (presetName: string) => {
    setIsDeleting(true);
    try {
      const presetInst = new Preset();
      await presetInst.delete(presetName);
      setshowDeleteConfirmation(false);
      if (selectedPreset) {
        void dispatch(deletePreset(selectedPreset));
      }
      setAlertMessage('Deleted Successfully');
    } catch (error) {
      console.error(error);
      setAlertMessage('Something went wrong');
    }
    setIsDeleting(false);
  };

  const getCount = (item: IModuleAceess[]) => {
    return item.filter((i: IModuleAceess) => {
      return customModules.findIndex((mod) => mod.moduleName === i.moduleName) === -1;
    }).length;
  };
  // On ceation on new preset
  const handleCreatePreset = (preset: IPreset) => {
    void dispatch(createPreset(preset));
  };
  // On change of any preset
  const handlePresetChange = (preset: IPreset) => {
    dispatch(changePreset(preset));
  };

  const resetPageAndSearch = (newQuery: string) => {
    setQuery(newQuery);
    handlePageChange(1);
  };
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleClosePresetShow = () => {
    setSelectedPreset(undefined);
    setShowModuleListPopup(false);
  };

  const handleClosePresetEdit = () => {
    setSelectedPreset(undefined);
    setShowEditModuleListPopup(false);
  };

  useEffect(() => {
    if (presetState.status !== 'loading') {
      void dispatch(getAllPresets());
    }
    if (selectedPresetFromSearch) {
      const index = presets.findIndex((preset) => preset.presetName === selectedPresetFromSearch);
      if (index !== -1) {
        const targetPage = Math.floor(index / recordPerPage) + 1;
        setCurrentPage(targetPage);
        setTimeout(() => {
          const row = document.getElementById(`preset-row-${selectedPresetFromSearch}`);
          row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
      setTimeout(() => {
        setSelectedPresetFromSearch(null);
      }, 1500);
    }
  }, []);

  return (
    <Suspense
      fallback={
        <div className="w-full flex justify-center align-middle items-center">
          <ReactLoading
            className="flex justify-center my-[4rem]"
            type="bubbles"
            color="#000000"
            height={100}
            width={350}
          />
        </div>
      }
    >
      <div className="w-full h-full md:w-full">
        <div className="bg-white block md:w-[100%] mx-auto md:ps-0.5 md:pe-0.5">
          <div className="flex gap-2 flex-wrap justify-between pt-2 pb-1">
            <Button
              className="h-fit min-w-fit text-white text-md cursor-pointer bg-black rounded-md px-4 py-2"
              onclick={() => {
                setShowCreatePopup(true);
              }}
            >
              Create Preset
            </Button>
            <div className="flex-grow-0">
              <Search
                onChange={(e: any) => resetPageAndSearch(e.target.value)}
                text="Search by preset..."
                hideLabel
              />
            </div>
          </div>
          <div className="w-full max-h-screen overflow-y-scroll">
            <div>
              <ConditionalLoader isLoading={isLoading} className="flex justify-center my-[4rem]">
                <>
                  {presets.length ? (
                    <div className="min-w-fit border rounded-md overflow-hidden">
                      <table className="w-full whitespace-nowrap rounded-lg bg-white divide-y divide-gray-300">
                        <thead className="bg-[#f8f8f8]">
                          <tr className="text-black text-center">
                            <th className="font-semibold text-sm uppercase px-6 py-4 text-left">
                              {' '}
                              Preset Name{' '}
                            </th>
                            <th className="font-semibold text-sm uppercase px-6 py-4 text-center">
                              {' '}
                              Number of Modules{' '}
                            </th>
                            <th className="font-semibold text-sm uppercase px-6 py-4 text-center">
                              {' '}
                              Action{' '}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {filteredPresets.map((item, index) => (
                            <tr
                              key={index}
                              id={`preset-row-${item.presetName}`}
                              className={`${
                                selectedPresetFromSearch === item.presetName ? 'bg-gray' : ''
                              } transition-colors`}
                            >
                              <td className="px-6 py-4 text-left">{item.presetName}</td>
                              <td className="px-6 py-4 text-center">
                                <Button
                                  onclick={() => {
                                    setSelectedPreset(item);
                                    setShowModuleListPopup(true);
                                  }}
                                >
                                  {getCount(item.moduleList)}
                                </Button>
                              </td>
                              <td className="px-6 py-4 text-center">
                                <Button
                                  onclick={() => {
                                    setSelectedPreset(item);
                                    setShowEditModuleListPopup(true);
                                  }}
                                >
                                  <ImageTag
                                    key="Edit"
                                    src={edit}
                                    width={'20px'}
                                    alt={''}
                                    name={''}
                                  />
                                </Button>
                                <Button
                                  onclick={() => {
                                    setSelectedPreset(item);
                                    setshowDeleteConfirmation(true);
                                  }}
                                >
                                  <ImageTag
                                    key="Delete"
                                    src={bin}
                                    width={'20px'}
                                    alt={''}
                                    name={''}
                                  />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-center">No Preset Found !!</p>
                  )}
                </>
              </ConditionalLoader>
            </div>
          </div>
        </div>
      </div>
      {showEditModuleListPopup && selectedPreset ? (
        <EditPresetForm
          preset={selectedPreset}
          show={showEditModuleListPopup}
          onClose={handleClosePresetEdit}
          onChange={handlePresetChange}
        />
      ) : (
        <></>
      )}
      {showModuleListPopup && selectedPreset ? (
        <ShowPreset
          preset={selectedPreset}
          show={showModuleListPopup}
          onClose={handleClosePresetShow}
        />
      ) : (
        <></>
      )}
      {showDeleteConfirmation && selectedPreset ? (
        showDeleteConfirmation && (
          <ConfirmationPopUp show={showDeleteConfirmation}>
            <div className="max-h-30% ">
              <IPPParagraph
                headerText={`Do you want to delete - ${selectedPreset.presetName}`}
                className={'mr-0 mb-0.5 ml-0 text-[23px]'}
              />
              <br />
              <div className="flex justify-center">
                <Button
                  className="h-10 px-4 py-[1.75] bg-white hover:bg-black hover:text-white text-[#2c323f] rounded focus:outline-none border border-spacing-1 border-black mr-3"
                  buttonText="Yes"
                  loading={isDeleting}
                  disabled={isDeleting}
                  onclick={() => {
                    void confirmDelete(selectedPreset.presetName);
                  }}
                />
                <Button
                  className="px-4 py-[1.75] bg-white hover:bg-black hover:text-white text-[#2c323f] rounded focus:outline-none border border-spacing-1 border-black ml-3"
                  buttonText="No"
                  onclick={cancelDelete}
                />
              </div>
            </div>
          </ConfirmationPopUp>
        )
      ) : (
        <></>
      )}
      <CustomPopup
        show={alertMessage.length > 0}
        onClose={() => {
          setAlertMessage('');
        }}
        width={'w-41%'}
      >
        <IPPParagraph className="" headerText={alertMessage} />
      </CustomPopup>
      {showCreatePopup && (
        <CreatePresetForm
          show={showCreatePopup}
          onClose={handleCloseCreatePopup}
          onCreate={handleCreatePreset}
        />
      )}
      {presets.length > recordPerPage && (
        <div>
          <Pagination
            className={'pagination-bar flex mt-4 justify-center items-center m-auto'}
            currentPage={currentPage}
            totalCount={presets.length}
            pageSize={recordPerPage}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </Suspense>
  );
};

export default PresetModules;
